/**
 * Dev/Staging 환경 전용 테스트 후크.
 *
 * E2E 테스트 및 개발자 콘솔 디버깅을 위해 핵심 모듈 인스턴스를 window에 노출한다.
 * 프로덕션 번들에서는 조건문이 컴파일 타임 상수로 평가되어 dead code가 제거된다.
 *
 * 사용 예 (E2E):
 *   await page.evaluate(() => window.__OMA_TEST__?.dataManager.checkKnownSender('x@y'))
 *
 * 주의:
 *  - 런타임 모듈 상태를 직접 건드릴 수 있으므로 프로덕션 노출은 절대 금지.
 *  - window property 이름은 안전한 prefix(__OMA_TEST__)로 고정해 충돌을 피한다.
 */

import dataManager from 'pages/main/data/DataManager'
import knownSenderCache from 'libs/CACHE/knownSenderCache'
import localforage, { infoDB } from 'myStore'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import * as calendarService from 'pages/calendar/modules/CalendarService'
import history from 'history'
import conf from 'conf'
import { getEnvParam, getReduxStore } from 'env'
import { logout as landingLogout } from 'services/landing'
import settings from 'popups/Settings'
import {
  setMaintenanceNowOverride,
  getMaintenanceNowOverride,
  setMaintenanceConfigOverride,
  getMaintenanceConfigOverride,
  clearNoticeDismissal,
  type NoticeConfig,
  type MaintenanceScreen,
} from 'services/maintenance'
import {
  getThreadItemDebugState,
  getVisibleEmailFormDebugState,
  invokeThreadItemDebugFoldClick,
  setThreadItemDebugFoldState,
  setThreadItemDebugPrevFold,
} from 'utils/devTestRegistry'

interface OmaTestAuthState {
  authorized: boolean
  loading: boolean
  pathname: string
}

interface OmaTestCalendarEvent {
  subject: string
  serverId: string
  startDate: string | null
  endDate: string | null
  uid: string | null
}

function reloadCalendarEvents(fullReload = true): void {
  calendarService.reloadEvents(fullReload)
}

function isCalendarSyncing(): boolean {
  return calendarService.isSyncing()
}

function getAuthState(): OmaTestAuthState {
  try {
    const reduxStore = getReduxStore()
    const state = reduxStore.getState() as {
      user?: {
        authorized?: boolean
        loading?: boolean
      }
    }

    return {
      authorized: state.user?.authorized === true,
      loading: state.user?.loading === true,
      pathname: window.location.pathname,
    }
  } catch {
    return {
      authorized: false,
      loading: false,
      pathname: window.location.pathname,
    }
  }
}

async function logoutAndRedirect(forced = true): Promise<boolean> {
  const didLogout = await landingLogout(forced)
  const reduxStore = getReduxStore()

  // localforage(로컬포리지)의 driver/local cache 경로를 직접 비워 token 판정이 남지 않게 한다.
  await localforage.clear().catch(() => {
    // 테스트 후크에서는 추가 정리 실패를 치명 에러로 보지 않는다.
  })
  await infoDB.clear().catch(() => {
    // infoDB 정리 실패도 동일하게 무시하고 다음 정리 경로를 진행한다.
  })

  // E2E에서 service-level logout만 호출하면 Redux auth state가 남아 보호 라우트 계약과
  // 어긋날 수 있다. 실제 앱의 logout 결과와 동일하게 user/menu 상태도 함께 초기화한다.
  reduxStore.dispatch({
    type: 'user/SET_STATE',
    payload: {
      id: '',
      name: '',
      role: '',
      email: '',
      avatar: '',
      authorized: false,
      workspace: null,
      loading: false,
      loginProcessing: false,
    },
  })
  reduxStore.dispatch({
    type: 'menu/RELOAD',
    payload: { loading: false },
  })

  if (window.location.pathname.indexOf('/auth/login') !== 0) {
    history.push('/auth/login')
  }

  return didLogout
}

function findCalendarEventBySubject(subject: string): OmaTestCalendarEvent | null {
  const instance = calendarService.getInstances().find((item) => item.subject === subject)
  if (instance == null) return null

  return {
    subject: instance.subject,
    serverId: instance.serverId,
    startDate: instance.startDate?.toISOString?.() ?? null,
    endDate: instance.endDate?.toISOString?.() ?? null,
    uid: instance.event?.uid ?? null,
  }
}

async function deleteCalendarEventBySubject(subject: string): Promise<boolean> {
  const instance = calendarService.getInstances().find((item) => item.subject === subject)
  if (instance == null) return false

  return await new Promise<boolean>((resolve) => {
    calendarService.deleteEvent(instance as any, calendarService.EventDeleteType.ALL, (success) => {
      resolve(success)
    })
  })
}

// notice.json URL('.../{env}/notice.json')에서 같은 디렉터리의 HTML 경로를 만든다.
// 예: '.../dev/notice.json' → '.../dev/blackout.html'
function maintenanceAssetUrl(file: string): string {
  return conf.SERVER.maintenanceURL.replace(/[^/]*$/, file)
}

// mock 설정 후 reload 없이 즉시 재평가되도록 FETCH 를 디스패치한다.
function refetchMaintenance(): void {
  const reduxStore = getReduxStore()
  reduxStore.dispatch({
    type: 'maintenance/FETCH',
    payload: { url: conf.SERVER.maintenanceURL },
  })
}

// 현재 평가된 화면 상태(redux)를 반환한다.
function getMaintenanceScreen(): MaintenanceScreen {
  try {
    const reduxStore = getReduxStore()
    const state = reduxStore.getState() as { maintenance?: { screen?: MaintenanceScreen } }
    return state.maintenance?.screen ?? { kind: 'none' }
  } catch {
    return { kind: 'none' }
  }
}

// 작업 공지(notice) 모달을 항상 띄우는 mock(기간 무한 + dismissal 초기화).
function showNoticeScenario(): void {
  clearNoticeDismissal()
  setMaintenanceConfigOverride({
    schema_version: 1,
    published_at: '2000-01-01T00:00:00Z',
    maintenance: {
      active: true,
      scope: ['all'],
      title: { ko: '점검 안내(테스트)', ja: 'メンテナンス(テスト)', en: 'Maintenance (test)' },
      notice_period: {
        url: maintenanceAssetUrl('notice.html'),
        start: '2000-01-01T00:00:00Z',
        end: '2099-01-01T00:00:00Z',
        modified_at: '2000-01-01T00:00:00Z',
      },
    },
  })
  refetchMaintenance()
}

// 점검 차단(blackout) 풀페이지를 띄우는 mock(기간 무한).
function showBlackoutScenario(): void {
  setMaintenanceConfigOverride({
    schema_version: 1,
    published_at: '2000-01-01T00:00:00Z',
    maintenance: {
      active: true,
      scope: ['all'],
      title: { ko: '점검 안내(테스트)', ja: 'メンテナンス(テスト)', en: 'Maintenance (test)' },
      blackout_period: {
        url: maintenanceAssetUrl('blackout.html'),
        start: '2000-01-01T00:00:00Z',
        end: '2099-01-01T00:00:00Z',
      },
    },
  })
  refetchMaintenance()
}

// 강제 업데이트(새로고침) 모달을 띄우는 mock(min_version 을 현재보다 높게).
function showForceUpdateScenario(): void {
  setMaintenanceConfigOverride({
    schema_version: 1,
    published_at: '2000-01-01T00:00:00Z',
    force_update: {
      url: maintenanceAssetUrl('force_update.html'),
      web: { min_version: '9999.0.0' },
    },
  })
  refetchMaintenance()
}

// 모든 오버라이드(mock/시간/dismissal) 해제 → 실제 notice.json 기준 정상 평가.
function resetMaintenanceScenario(): void {
  setMaintenanceConfigOverride(null)
  setMaintenanceNowOverride(null)
  clearNoticeDismissal()
  refetchMaintenance()
}

declare global {
  interface Window {
    __OMA_TEST__?: {
      dataManager: typeof dataManager
      knownSenderCache: typeof knownSenderCache
      orgChartHelper: typeof orgChartHelper
      contactHelper: typeof contactHelper
      getUserEmail: () => string
      logout: (forced?: boolean) => Promise<boolean>
      logoutAndRedirect: (forced?: boolean) => Promise<boolean>
      getAuthState: () => OmaTestAuthState
      /** 설정 팝업을 지정한 탭 경로로 즉시 연다. 예: ['accounts', 'appPassword'] */
      openSettings: (selectedTabs?: string[]) => void
      calendar: {
        findEventBySubject: (subject: string) => OmaTestCalendarEvent | null
        deleteEventBySubject: (subject: string) => Promise<boolean>
        reloadEvents: (fullReload?: boolean) => void
        isSyncing: () => boolean
      }
      emailForm: {
        getVisibleState: () => { isSending: boolean; invisible: boolean } | null
      }
      threadItem: {
        getState: (key: string) => { fold: boolean; expanded: boolean; prevFold?: boolean } | null
        setFoldState: (key: string, fold: boolean) => boolean
        setPrevFold: (key: string, prevFold?: boolean) => boolean
        invokeFoldClick: (key: string, times?: number) => boolean
      }
      maintenance: {
        /** 시간 오버라이드 설정 후 새로고침하면 해당 시각 기준으로 공지/점검을 평가한다. */
        setNow: (iso: string) => void
        /** 시간 오버라이드 해제 후 새로고침. */
        clearNow: () => void
        /** 현재 오버라이드 값(없으면 null). */
        getNow: () => string | null
        /** notice.json mock 설정 후 새로고침(CORS 미설정 환경 테스트용). */
        setMockConfig: (config: NoticeConfig) => void
        /** notice.json mock 해제 후 새로고침. */
        clearMockConfig: () => void
        /** 현재 mock notice.json(없으면 null). */
        getMockConfig: () => NoticeConfig | null
        /** 작업 공지 모달을 즉시 표시(reload 불필요). */
        showNotice: () => void
        /** 점검 차단(blackout) 풀페이지를 즉시 표시. */
        showBlackout: () => void
        /** 강제 업데이트(새로고침) 모달을 즉시 표시. */
        showForceUpdate: () => void
        /** 모든 오버라이드 해제 → 정상(none) 복귀. */
        reset: () => void
        /** 현재 평가된 화면 상태({ kind: ... }) 확인. */
        getScreen: () => MaintenanceScreen
      }
    }
  }
}

if (!__ENV_PRODUCTION__) {
  window.__OMA_TEST__ = {
    dataManager,
    knownSenderCache,
    orgChartHelper,
    contactHelper,
    getUserEmail: () => getEnvParam('userEmail') as string,
    openSettings: (selectedTabs = []) => settings.open({ selectedTabs }),
    logout: (forced = false) => landingLogout(forced),
    logoutAndRedirect,
    getAuthState,
    calendar: {
      findEventBySubject: (subject) => findCalendarEventBySubject(subject),
      deleteEventBySubject: (subject) => deleteCalendarEventBySubject(subject),
      reloadEvents: (fullReload = true) => reloadCalendarEvents(fullReload),
      isSyncing: () => isCalendarSyncing(),
    },
    emailForm: {
      getVisibleState: () => getVisibleEmailFormDebugState(),
    },
    threadItem: {
      getState: (key) => getThreadItemDebugState(key),
      setFoldState: (key, fold) => setThreadItemDebugFoldState(key, fold),
      setPrevFold: (key, prevFold) => setThreadItemDebugPrevFold(key, prevFold),
      invokeFoldClick: (key, times = 1) => invokeThreadItemDebugFoldClick(key, times),
    },
    maintenance: {
      setNow: (iso) => setMaintenanceNowOverride(iso),
      clearNow: () => setMaintenanceNowOverride(null),
      getNow: () => getMaintenanceNowOverride(),
      setMockConfig: (config) => setMaintenanceConfigOverride(config),
      clearMockConfig: () => setMaintenanceConfigOverride(null),
      getMockConfig: () => getMaintenanceConfigOverride(),
      showNotice: () => showNoticeScenario(),
      showBlackout: () => showBlackoutScenario(),
      showForceUpdate: () => showForceUpdateScenario(),
      reset: () => resetMaintenanceScenario(),
      getScreen: () => getMaintenanceScreen(),
    },
  }
}

export {}
